import CommuteIcon from "@mui/icons-material/Commute";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import { formatDate } from "@repo/utils/date";
import DriverDetailsItem from "../DriverDetails/Item";
import type { TDriverData, TTermsData } from "./types";

export default function DriverTermsState({
  driverData,
  termsData,
}: {
  driverData: TDriverData;
  termsData?: TTermsData;
}) {
  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <Grid
          container
          sx={{
            backgroundColor: "#E7E6F7",
            color: "#fff",
            borderRadius: 1,
            padding: "20px",
            height: "100%",
          }}
        >
          <Grid container size={{ xs: 12 }} spacing={2}>
            <Stack marginY={2} direction="row" spacing={2} alignItems="center" color="#00244f">
              <CommuteIcon />
              <Typography variant="h5" component="h4">
                Deals
              </Typography>
            </Stack>
          </Grid>
          <Grid
            container
            spacing={2}
            sx={{
              color: "#00244f",
            }}
          >
            {[
              { title: "Nombre de betdeals", number: driverData.nbDeals },
              { title: "Nombre de lossdeals", number: driverData.lossDeals },
              { title: "Nombre de dealsinstock", number: driverData.stokeDeals },
            ].map((deal) => (
              <Grid size={{ xs: 6 }} key={`driver-delivery-deal-item-${deal.title}`}>
                <DriverDetailsItem title={deal.title}>
                  {deal.number ?? (
                    <div style={{ fontSize: "var(--medium-font-size)" }}>Pas encore d'offres</div>
                  )}
                </DriverDetailsItem>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Grid
          container
          sx={{
            backgroundColor: "#E7E6F7",
            color: "#00244f",
            borderRadius: 1,
            padding: "20px",
            height: "100%",
          }}
        >
          <Grid size={{ xs: 12 }}>
            <Stack margin={2} direction="row" spacing={2} alignItems="center">
              <ReceiptIcon />
              <Typography variant="h5" component="h4">
                Factures
              </Typography>
            </Stack>
          </Grid>

          <Grid
            container
            spacing={2}
            sx={{
              color: "#00244f",
            }}
          >
            {[
              {
                title: "Total facturé",
                billing: driverData.driverOffersPrice,
              },
              {
                title: "Facturation en attente",
                billing: driverData.driverAttentePrice,
              },
            ].map((invoice) => {
              return (
                <Grid size={{ xs: 6 }} key={`driver-delivery-invoice-item-${invoice.title}`}>
                  <DriverDetailsItem title={invoice.title}>
                    {invoice.billing ? (
                      <>{invoice.billing.toFixed(2)} €</>
                    ) : (
                      <div style={{ fontSize: "var(--medium-font-size)" }}>
                        Pas encore de factures
                      </div>
                    )}
                  </DriverDetailsItem>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>

      {/* Terms and Conditions Section */}
      {termsData && (
        <Grid size={{ xs: 12 }}>
          <Grid
            container
            sx={{
              backgroundColor: "#E7E6F7",
              color: "#00244f",
              borderRadius: 1,
              padding: "20px",
              height: "100%",
            }}
          >
            <Grid size={{ xs: 12 }}>
              <Stack marginY={2} direction="row" spacing={2} alignItems="center">
                <ReceiptIcon />
                <Typography variant="h5" component="h4">
                  Conditions générales
                </Typography>
                {termsData && (
                  <Typography
                    variant="body2"
                    ml={2}
                    color={termsData.allAccepted ? "success.main" : "warning.main"}
                  >
                    {termsData.allAccepted ? "Toutes acceptées" : "En attente"}
                  </Typography>
                )}
                <Box flexGrow={1} />
                {termsData?.lastUpdated && (
                  <Typography variant="caption" color="text.secondary">
                    Dernière mise à jour : {formatDate(termsData.lastUpdated)}
                  </Typography>
                )}
              </Stack>
            </Grid>
            <Grid container spacing={2}>
              {termsData?.sections?.map((section) => (
                <Grid size={{ xs: 6, md: 6 }} key={section.id}>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: section.accepted ? "success.light" : "warning.light",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                      backgroundColor: section.accepted ? "#f6fff7" : "#fff8e1",
                      height: "100%",
                      boxShadow: 1,
                      transition: "box-shadow 0.2s",
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Typography variant="subtitle1" fontWeight={600} flex={1}>
                        {section.title}
                      </Typography>
                      <Box>
                        <Chip
                          size="small"
                          color={section.accepted ? "success" : "warning"}
                          label={section.accepted ? "Acceptée" : "Non acceptée"}
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                    </Stack>
                    <Box mb={1}>
                      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2, // Number of lines to show
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {typeof section.content === "string"
                          ? section.content
                          : JSON.stringify(section.content)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
}
